import { Button, Icon, Modal } from '@octano/global-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Table } from 'reactstrap';

import { TranslationsKeys } from '../../../locales/translations';
import { downloadVersionExcel, getLiquidationHistory } from '../api';
import { LiquidationVersionSummary } from '../types';

interface LiquidationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  periodId: number | null;
}

export const LiquidationHistoryModal = ({
  isOpen,
  onClose,
  periodId,
}: LiquidationHistoryModalProps) => {
  const { t } = useTranslation(TranslationsKeys.CREDIT_LIQUIDATION_INITIAL);
  const [versions, setVersions] = useState<LiquidationVersionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    const loadHistory = async () => {
      if (!periodId || !isOpen) {
        return;
      }
      setLoading(true);
      try {
        const response = await getLiquidationHistory(periodId);
        setVersions(response.data || []);
      } catch (error) {
        console.error('Error loading history:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && periodId) {
      loadHistory();
    }
  }, [isOpen, periodId]);

  const handleDownload = async (version: LiquidationVersionSummary) => {
    if (!periodId) {
      return;
    }
    setDownloadingId(version.id);
    try {
      await downloadVersionExcel(version.id, periodId);
    } catch (error) {
      console.error('Error downloading:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleClose = () => {
    setVersions([]);
    onClose();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl">
      <div className="p-4">
        <Row className="text-center mb-4">
          <Col xs={12}>
            <Icon name="information" size="48px" color="primary" />
          </Col>
          <Col xs={12} className="mt-2">
            <h4>{t('history.title')}</h4>
            <span className="text-muted">{t('history.subtitle')}</span>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <span>{t('history.loading')}</span>
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-5">
            <span>{t('history.empty')}</span>
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped responsive>
              <thead>
                <tr>
                  <th>{t('history.columns.date')}</th>
                  <th>{t('history.columns.generatedBy')}</th>
                  <th>{t('history.columns.period')}</th>
                  <th>{t('history.columns.faculty')}</th>
                  <th>{t('history.columns.program')}</th>
                  <th>{t('history.columns.campus')}</th>
                  <th>{t('history.columns.schedule')}</th>
                  <th>{t('history.columns.download')}</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr key={version.id}>
                    <td>{formatDate(version.executedAt)}</td>
                    <td>{version.executedByName || '-'}</td>
                    <td>{version.periodName || '-'}</td>
                    <td>
                      {version.filters?.schoolName || t('history.allFilter')}
                    </td>
                    <td>
                      {version.filters?.studyPlanName || t('history.allFilter')}
                    </td>
                    <td>
                      {version.filters?.campusName || t('history.allFilter')}
                    </td>
                    <td>
                      {version.filters?.scheduleName || t('history.allFilter')}
                    </td>
                    <td>
                      <Button
                        text={t('history.download')}
                        size="sm"
                        icon="download"
                        onClick={() => handleDownload(version)}
                        loading={downloadingId === version.id}
                        disabled={downloadingId !== null}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <Row className="mt-4">
          <Col xs={12}>
            <Button
              text={t('history.close')}
              fullwidth
              onClick={handleClose}
              color="primary"
            />
          </Col>
        </Row>
      </div>
    </Modal>
  );
};
